import { Translations } from './index';

export const enUS: Translations = {
  app: {
    name: 'Event Finder',
    tagline: 'Discover Events Worldwide',
    description: 'Discover and explore events from organizations, universities, and cities with AI-powered search',
  },

  header: {
    searchPlaceholder: 'Search for events, topics, or locations...',
    searchingEvents: 'Searching for events...',
    noSuggestionsFound: 'No suggestions found',
    searchButton: 'Search',
    categoriesView: 'Categories',
    mapView: 'Events Near Me',
    calendarView: 'Calendar View',
    feedback: 'Feedback',
    createGroup: 'Create Event Groups',
    myEvents: 'My Events',
    menu: 'Menu',
    howItWorks: 'How It Works',
    latestUpdates: 'Latest Updates',
    upcomingShort: 'upcoming',
  },

  suggestions: {
    locations: 'Locations',
    entities: 'Tech Companies, Corporations, Universities & Organizations',
    events: 'Events',
    location: 'Location',
    relevance: 'Relevance',
  },

  homepage: {
    heroTitle: 'Discover Amazing Events',
    heroSubtitle: 'Find and explore events that match your interests with our AI-powered search',
    popularEvents: 'Popular Events',
    topCities: 'Top Cities',
    viewAllEvents: 'View All Events',
    exploreEvents: 'Explore Events',
    discoverEvents: 'Discover Events',
    findEventsNearby: 'Find Events Nearby',
    liveStats: {
      ariaLabel: 'Current Event Finder activity',
      upcoming: 'Upcoming',
      inPerson: 'In person',
      virtual: 'Virtual',
      hybrid: 'Hybrid',
      organizations: 'Organizations',
      partners: 'tech companies',
      companies: 'corporations',
      webSources: 'Web sources',
      cities: 'cities',
      countries: 'countries',
      continents: 'continents',
      addedPastWeek: 'added in 7 days',
      updated: 'Updated',
      fresh: 'Fresh data',
      loading: 'Loading current activity',
    },
  },

  events: {
    upcoming: 'Upcoming',
    past: 'Past',
    virtual: 'Virtual',
    inPerson: 'In Person',
    free: 'Free',
    paid: 'Paid',
    eventDetails: 'Event Details',
    venue: 'Venue',
    date: 'Date',
    time: 'Time',
    location: 'Location',
    organizer: 'Organizer',
    category: 'Category',
    register: 'Register',
    moreInfo: 'More Info',
    noEventsFound: 'No events found',
    loadingEvents: 'Loading events...',
    relatedTo: 'Related to',
    duration: 'Duration',
    eventSource: 'Event Source',
    universityHosted: 'University-hosted',
    universityFallbackName: 'a university',
    universityAttendanceNotice: 'This event is hosted by {university}. University events may have attendance or eligibility restrictions. Check the event page or registration details before attending.',
    universityCampusPrecision: 'campus-level (University host)',
  },

  browse: {
    title: 'Browse Events',
    searchResultsTitle: 'Search Results for "{query}"',
    searchResultsDescription: 'Discover events related to "{query}" using AI-powered search',
    noResultsTitle: 'No events found',
    noResultsDescription: 'Try adjusting your search criteria or filters',
    showingResults: 'Showing {count} of {total} events',
    filtersApplied: 'Filters applied',
  },

  map: {
    title: 'Events Near Me',
    subtitle: 'Explore events around the world',
    eventsNearby: 'Events Nearby',
    showingEvents: 'Showing events',
    zoomToArea: 'Zoom to area',
    currentLocation: 'Current Location',
    enableLocation: 'Enable Location',
    failedToLoadEvents: 'Failed to load events. Please try again later.',
    failedToGetLocation: 'Failed to get your location. Please enable location services.',
  },

  calendar: {
    title: 'Event Calendar',
    subtitle: 'View events by date',
    monthView: 'Month View',
    weekView: 'Week View',
    dayView: 'Day View',
    today: 'Today',
    nextMonth: 'Next Month',
    previousMonth: 'Previous Month',
    eventsToday: 'Events Today',
    noEventsThisDay: 'No events scheduled for this day',
  },

  filters: {
    filterBy: 'Filter By',
    dateRange: 'Date Range',
    location: 'Location',
    category: 'Category',
    price: 'Price',
    eventType: 'Event Type',
    clearFilters: 'Clear Filters',
    applyFilters: 'Apply Filters',
    startDate: 'Start Date',
    endDate: 'End Date',
    anyPrice: 'Any Price',
    freeOnly: 'Free Only',
    paidOnly: 'Paid Only',
    anyType: 'Any Type',
    virtualOnly: 'Virtual Only',
    inPersonOnly: 'In Person Only',
  },

  actions: {
    search: 'Search',
    clear: 'Clear',
    cancel: 'Cancel',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    share: 'Share',
    export: 'Export',
    print: 'Print',
    refresh: 'Refresh',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    close: 'Close',
    submit: 'Submit',
  },

  time: {
    today: 'Today',
    tomorrow: 'Tomorrow',
    yesterday: 'Yesterday',
    thisWeek: 'This Week',
    nextWeek: 'Next Week',
    thisMonth: 'This Month',
    nextMonth: 'Next Month',
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening',
    night: 'Night',
    days: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
    },
    months: {
      january: 'January',
      february: 'February',
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December',
    },
  },

  errors: {
    general: 'An error occurred. Please try again.',
    networkError: 'Network error. Please check your connection.',
    notFound: 'The requested item was not found.',
    unauthorized: 'You are not authorized to access this resource.',
    serverError: 'Server error. Please try again later.',
    invalidInput: 'Invalid input. Please check your data.',
    tryAgainLater: 'Please try again later.',
    pageNotFound: 'Page not found.',
    locationError: 'Unable to access your location.',
  },

  success: {
    eventSaved: 'Event saved successfully!',
    filtersSaved: 'Filters saved successfully!',
    settingsUpdated: 'Settings updated successfully!',
    feedbackSubmitted: 'Feedback submitted successfully!',
  },

  loading: {
    general: 'Loading...',
    events: 'Loading events...',
    suggestions: 'Loading suggestions...',
    map: 'Loading map...',
    calendar: 'Loading calendar...',
    searching: 'Searching...',
  },

  howItWorks: {
    title: 'How Event Finder Works',
    subtitle: 'AI-Powered Event Discovery at Scale',
    heroTitle: 'Event Finder: AI-Powered Event Discovery at Scale',
    heroSubtitle: 'Discover how we use cutting-edge AI technology to find events from thousands of websites worldwide',
    
    challengeTitle: 'The Challenge: Finding Needles in the Digital Haystack',
    challengeDescription: 'Imagine trying to manually monitor thousands of event websites across the globe to discover upcoming events, conferences, workshops, and training sessions. Each website has different layouts, update schedules, and formats. Some announce events months in advance, others just weeks. Missing a single important event could mean losing valuable business opportunities or failing to stay ahead of industry trends.',
    
    solutionTitle: 'The AI-Powered Solution: Intelligent Web Discovery',
    solutionDescription: 'Event Finder transforms this impossible manual task into an automated, intelligent discovery system that works 24/7.',
    
    aiVisionTitle: '🧠 Dual AI Vision System',
    aiVisionDescription: 'The system employs two complementary AI approaches to ensure no event goes undetected:',
    
    primaryMethodTitle: '1. Markdown Intelligence',
    primaryMethodSubtitle: 'Primary Method',
    primaryMethodDescription: 'Our primary approach converts complex websites into clean, structured data for AI analysis.',
    primaryMethodFeatures: {
      webScraping: 'Advanced Web Scraping: Converts complex HTML websites into clean, structured markdown text',
      gptProcessing: 'GPT-4o-mini Processing: Uses recent OpenAI LLMs to understand and extract event information from the text',
      contextUnderstanding: 'Context Understanding: The AI doesn\'t just look for keywords—it understands context, dates, locations, and event types'
    },
    
    fallbackMethodTitle: '2. Visual AI Analysis',
    fallbackMethodSubtitle: 'Fallback Method',
    fallbackMethodDescription: 'When text analysis isn\'t sufficient, our visual AI steps in to interpret website content like a human would.',
    fallbackMethodFeatures: {
      screenshotTechnology: 'Screenshot Technology: Captures visual snapshots of websites using Chrome automation',
      computerVision: 'Computer Vision: Uses GPT-4o with vision capabilities to "see" and interpret website content like a human would',
      layoutIntelligence: 'Layout Intelligence: Recognizes event listings, calendars, and announcements regardless of website design'
    },
    
    smartMatchingTitle: '🎯 Smart Event Matching and Deduplication',
    smartMatchingDescription: 'The real power comes from preventing information overload:',
    
    vectorSimilarityTitle: 'Vector Similarity Matching',
    vectorSimilarityFeatures: {
      semanticUnderstanding: 'Semantic Understanding: Uses advanced embeddings to understand that "Azure Workshop" and "Microsoft Cloud Training" might be the same event',
      intelligentDeduplication: 'Intelligent Deduplication: Prevents the same event from being recorded multiple times across different event websites',
      confidenceScoring: 'Confidence Scoring: Each match gets a confidence score, ensuring accuracy'
    },
    
    fuzzyLogicTitle: 'Fuzzy Logic Processing',
    fuzzyLogicFeatures: {
      dateIntelligence: 'Date Intelligence: Understands that "Next Tuesday" and "March 15th" might refer to the same date',
      locationMatching: 'Location Matching: Recognizes that "Seattle, WA" and "Seattle, Washington" are the same place',
      titleVariations: 'Title Variations: Catches events even when corporations use slightly different names'
    },
    
    processingPipelineTitle: '🔄 Intelligent Processing Pipeline',
    processingPipelineDescription: 'The system operates through a sophisticated queue-based architecture that processes thousands of websites efficiently:',
    processingPipelineFeatures: {
      qualityControl: 'Automated Quality Control',
      freshnessFiltering: 'Freshness Filtering: Automatically removes events older than 2 weeks to keep data current',
      dataValidation: 'Data Validation: Ensures event information is complete and properly formatted',
      errorRecovery: 'Error Recovery: If one method fails, the system automatically tries alternative approaches'
    },
    
    cloudArchitectureTitle: '🚀 Scalable Cloud Architecture',
    cloudArchitectureDescription: 'Built on Azure\'s enterprise-grade infrastructure:',
    cloudArchitectureFeatures: {
      parallelProcessing: 'Parallel Processing: Can analyze hundreds of websites simultaneously',
      autoScaling: 'Auto-scaling: Automatically adjusts resources based on workload',
      faultTolerance: 'Fault Tolerance: Continues processing even if individual websites are down',
      globalReach: 'Global Reach: Processes websites from around the world in multiple time zones'
    },
    
    resultsTitle: 'The Incredible Results',
    resultsDescription: 'Our AI-powered system delivers exceptional performance and business value.',
    
    businessValueTitle: '💡 Business Value',
    businessValueForBD: 'For Business Development: Never miss collaboration opportunities, discover joint events and collaboration possibilities, get competitive intelligence, and see industry trends months in advance.',
    businessValueForMarketing: 'For Marketing Teams: Find content opportunities, event sponsorships, speaking opportunities, audience insights, and high-value events for participation.',
    businessValueForSales: 'For Sales Teams: Generate leads from event attendees, build relationships at industry gatherings, and gain market intelligence about customer needs.',
    
    technologyStackTitle: 'The Technology Stack: Best-in-Class AI',
    azureOpenAITitle: '🤖 Azure OpenAI Integration: GPT-4o & GPT-4o-mini for content understanding, text embedding models for semantic similarity, and vision capabilities for visual content processing.',
    azureSearchTitle: '🔍 Azure AI Search: Vector search to find similar events, semantic search for intent understanding, and real-time indexing of new events.',
    analyticsTitle: '📊 Comprehensive Analytics: Application Insights for monitoring, custom dashboards for tracking trends, and quality metrics for measuring accuracy.',
    
    realWorldImpactTitle: 'Real-World Impact',
    realWorldImpactDescription: 'Imagine discovering that three of your key tech companies are hosting AI conferences in the same month, giving you opportunities to coordinate marketing, plan complementary events, avoid conflicts, and build collaborations. Or finding emerging technology trends weeks before they become mainstream.',
    
    ctaTitle: 'Ready to unlock the hidden intelligence in thousands of websites?',
    ctaDescription: 'The events of tomorrow are being planned today—make sure you\'re the first to know about them.',
    ctaButton: 'Explore Events Now'
  },

  categories: {
    technology: 'Technology',
    business: 'Business',
    education: 'Education',
    healthcare: 'Healthcare',
    entertainment: 'Entertainment',
    sports: 'Sports',
    arts: 'Arts',
    science: 'Science',
    community: 'Community',
    networking: 'Networking',
    conference: 'Conference',
    workshop: 'Workshop',
    seminar: 'Seminar',
    meetup: 'Meetup',
    webinar: 'Webinar',
  },

  footer: {
    companyName: 'Event Finder',
    companyDescription: 'Discover and connect with amazing events worldwide. From local meetups to international conferences, we help you find experiences that matter to you.',
    contactEmail: 'hello@eventfinder.site',
    contactPhone: '+1 (555) 123-EVENT',
    contactAddress: 'New York, NY',
    
    // CTA Section
    ctaTitle: 'Ready to Discover Amazing Events?',
    ctaDescription: 'Use the feedback button in the header to share your thoughts and help us improve your event discovery experience.',
    ctaButton: 'Give Feedback',
    
    // Quick Links Section
    quickLinksTitle: 'Quick Links',
    aboutUs: 'About Us',
    ourServices: 'Our Services',
    blogResources: 'Blog & Resources',
    latestUpdates: 'Latest Updates',
    careers: 'Careers',
    helpCenter: 'Help Center',
    contact: 'Contact',
    
    // Platform Section
    platformTitle: 'Platform',
    browseEvents: 'Browse Events',
    eventCalendar: 'Event Calendar',
    advancedSearch: 'Advanced Search',
    apiDocumentation: 'API Documentation',
    mobileApp: 'Mobile App',
    siteMap: 'Site Map',
    siteStatistics: 'Site Statistics',
    
    // Trust & Safety Section
    trustSafetyTitle: 'Trust & Safety',
    securityCenter: 'Security Center',
    accessibility: 'Accessibility',
    communityGuidelines: 'Community Guidelines',
    trustSafety: 'Trust & Safety',
    reportIssue: 'Report Issue',
    
    // Trust Badges
    sslSecured: 'SSL Secured',
    iso27001: 'ISO 27001',
    gdprCompliant: 'GDPR Compliant',
    
    // Legal Links
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    cookiePolicy: 'Cookie Policy',
    disclaimer: 'Disclaimer',
    
    // Copyright
    copyright: '©',
    allRightsReserved: 'All rights reserved',
  },

  regional: {
    currency: 'USD',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12-hour',
    numberFormat: '1,234.56',
    phoneFormat: '(555) 123-4567',
    addressFormat: 'Street, City, State ZIP',
  },
};
